import { WarningIcon } from "@/assets/icons";
import { cn } from "@/lib/utils";

type ToneAlertProps = {
  /** `warning` is something the user can still accept, `error` is a failure. */
  tone: "warning" | "error";
  /** `compact` holds a single line of copy, `detailed` makes room for a title and a footnote. */
  variant?: "compact" | "detailed";
  title?: string;
  /** Consequence of accepting or leaving it as is, under a divider. */
  footnote?: string;
  className?: string;
  /** Set it when a test has to reach the icon rather than the alert around it. */
  iconTestId?: string;
  children: React.ReactNode;
  "data-testid"?: string;
};

// The compact variant leans on a full-strength border to stand out on its own;
// the detailed one gets its weight from padding, so its border stays pale.
const TONE_CLASSNAME = {
  warning: {
    container: "bg-orange-50",
    icon: "fill-tangerine",
    border: { compact: "border-tangerine", detailed: "border-orange-200" }
  },
  error: {
    container: "bg-red-50",
    icon: "fill-cinnabar",
    border: { compact: "border-cinnabar", detailed: "border-red-200" }
  }
};

const VARIANT_CLASSNAME = {
  compact: {
    container: "rounded-lg p-2",
    icon: "mt-0.5 size-4.5",
    body: "text-[10px] leading-4"
  },
  detailed: {
    container: "rounded-xl p-3",
    icon: "w-5 h-5",
    body: "text-xs"
  }
};

const ToneAlert = ({
  tone,
  variant = "detailed",
  title,
  footnote,
  className,
  iconTestId,
  children,
  ...rest
}: ToneAlertProps) => {
  const toneClassName = TONE_CLASSNAME[tone];
  const variantClassName = VARIANT_CLASSNAME[variant];
  const borderClassName = toneClassName.border[variant];

  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-2 border",
        toneClassName.container,
        variantClassName.container,
        borderClassName,
        className
      )}
      {...rest}
    >
      <WarningIcon
        className={cn("shrink-0", variantClassName.icon, toneClassName.icon)}
        data-testid={iconTestId}
      />
      <div className="flex flex-col gap-1">
        {title && (
          <span className="text-sm font-semibold text-txtHeading">{title}</span>
        )}
        <span className={cn("text-txtHeading", variantClassName.body)}>
          {children}
        </span>
        {footnote && (
          <span
            className={cn(
              "text-xs text-dim-gray border-t pt-2 mt-1",
              borderClassName
            )}
          >
            {footnote}
          </span>
        )}
      </div>
    </div>
  );
};

export default ToneAlert;
